import { useState, useEffect } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger, DrawerFooter, DrawerClose } from "@/components/ui/drawer";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { DatePicker } from "@/components/ui/date-picker";
import { CompoundSelect } from "@/components/ui/compound-select";
import { toast } from "sonner";
import { format, parseISO, differenceInDays } from "date-fns";
import { Plus, FileText, Calendar, Clock, Trash2, Edit, Loader2, Pause, Play, TrendingUp, ExternalLink, AlertTriangle } from "lucide-react";
import AppLogo from "@/components/AppLogo";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const SAMPLE_PROTOCOLS_URL = "https://drive.google.com/drive/folders/1pSJamLikTEEGbxStFgo2WVHPEVdsCda2";

// Helper to calculate dose-based progress (not calendar days)
const calculateDoseProgress = (protocol) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startDate = parseISO(protocol.start_date);
  const endDate = parseISO(protocol.end_date);
  
  const frequency = protocol.frequency;
  const customDays = protocol.custom_days || [];
  
  // Calculate total number of scheduled doses
  let totalDoses = 0;
  let currentDose = 0;
  let checkDate = new Date(startDate);
  
  while (checkDate <= endDate) {
    let isScheduled = false;
    
    if (frequency === "daily") {
      isScheduled = true;
    } else if (frequency === "every_other_day") {
      const daysDiff = differenceInDays(checkDate, startDate);
      isScheduled = daysDiff % 2 === 0;
    } else if (frequency === "weekly") {
      isScheduled = checkDate.getDay() === startDate.getDay();
    } else if (frequency === "custom") {
      // JavaScript: Sunday = 0, Monday = 1, etc.
      // Our custom_days: Monday = 0, Tuesday = 1, etc.
      const jsDay = checkDate.getDay();
      const ourDay = jsDay === 0 ? 6 : jsDay - 1; // Convert JS day to our format
      isScheduled = customDays.includes(ourDay);
    }
    
    if (isScheduled) {
      totalDoses++;
      if (checkDate <= today) {
        currentDose++;
      }
    }
    
    checkDate.setDate(checkDate.getDate() + 1);
  }
  
  if (totalDoses === 0) return { currentDose: 0, totalDoses: 0, percent: 0 };
  if (currentDose < 1) return { currentDose: 0, totalDoses, percent: 0 };
  if (currentDose > totalDoses) return { currentDose: totalDoses, totalDoses, percent: 100 };
  
  const percent = Math.round((currentDose / totalDoses) * 100);
  return { currentDose, totalDoses, percent };
};

const DOSE_UNITS = ["mg", "mcg", "IU", "units"];
const FREQUENCIES = [
  { value: "daily", label: "Daily" },
  { value: "every_other_day", label: "Every Other Day" },
  { value: "weekly", label: "Weekly" },
  { value: "custom", label: "Custom Days" },
];
const CYCLE_UNITS = ["days", "weeks"];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const initialFormState = {
  compound_name: "",
  dose: "",
  dose_unit: "mg",
  frequency: "daily",
  custom_days: [],
  time_of_day: "08:00",
  start_date: format(new Date(), "yyyy-MM-dd"),
  cycle_length: 28,
  cycle_unit: "days",
  notes: "",
};

export default function ProtocolsPage() {
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingProtocol, setEditingProtocol] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [protocolToDelete, setProtocolToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchProtocols = async () => {
    try {
      const res = await api.get("/protocols");
      setProtocols(res.data);
    } catch (error) {
      toast.error("Failed to load protocols");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProtocols();
  }, []);

  const resetForm = () => {
    setForm(initialFormState);
    setEditingProtocol(null);
  };

  const openEditDrawer = (protocol) => {
    setEditingProtocol(protocol);
    setForm({
      compound_name: protocol.compound_name,
      dose: protocol.dose.toString(),
      dose_unit: protocol.dose_unit,
      frequency: protocol.frequency,
      custom_days: protocol.custom_days || [],
      time_of_day: protocol.time_of_day,
      start_date: protocol.start_date,
      cycle_length: protocol.cycle_length,
      cycle_unit: protocol.cycle_unit,
      notes: protocol.notes || "",
    });
    setDrawerOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.compound_name || !form.dose) {
      toast.error("Please fill in required fields");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        dose: parseFloat(form.dose),
        custom_days: form.frequency === "custom" ? form.custom_days : null,
      };

      if (editingProtocol) {
        await api.put(`/protocols/${editingProtocol.id}`, payload);
        toast.success("Protocol updated");
      } else {
        await api.post("/protocols", payload);
        toast.success("Protocol created");
      }

      setDrawerOpen(false);
      resetForm();
      fetchProtocols();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save protocol");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!protocolToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`/protocols/${protocolToDelete.id}`);
      toast.success("Protocol deleted");
      setDeleteDialogOpen(false);
      setProtocolToDelete(null);
      fetchProtocols();
    } catch (error) {
      toast.error("Failed to delete protocol");
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteDialog = (protocol) => {
    setProtocolToDelete(protocol);
    setDeleteDialogOpen(true);
  };

  const toggleActive = async (protocol) => {
    try {
      await api.put(`/protocols/${protocol.id}`, { is_active: !protocol.is_active });
      toast.success(protocol.is_active ? "Protocol paused" : "Protocol resumed");
      fetchProtocols();
    } catch (error) {
      toast.error("Failed to update protocol");
    }
  };

  const toggleCustomDay = (dayIndex) => {
    setForm(prev => ({
      ...prev,
      custom_days: prev.custom_days.includes(dayIndex)
        ? prev.custom_days.filter(d => d !== dayIndex)
        : [...prev.custom_days, dayIndex]
    }));
  };

  return (
    <div className="page-container" data-testid="protocols-page">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <AppLogo size="small" showText={false} />
            <div>
              <h1 className="font-heading text-lg font-bold">Protocols</h1>
              <a 
                href={SAMPLE_PROTOCOLS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-primary hover:underline"
              >
                View sample protocols by Leks LD
                <ExternalLink size={10} />
              </a>
            </div>
          </div>
          <Drawer open={drawerOpen} onOpenChange={(open) => {
            setDrawerOpen(open);
            if (!open) resetForm();
          }}>
            <DrawerTrigger asChild>
              <Button className="rounded-full" data-testid="add-protocol-btn">
                <Plus size={18} className="mr-2" />
                New
              </Button>
            </DrawerTrigger>
            <DrawerContent className="max-h-[90vh]">
              <DrawerHeader>
                <DrawerTitle className="font-heading">
                  {editingProtocol ? "Edit Protocol" : "New Protocol"}
                </DrawerTitle>
              </DrawerHeader>
              
              <div className="overflow-y-auto px-4 pb-4 space-y-6">
                {/* Compound Name */}
                <div className="space-y-2">
                  <Label htmlFor="compound_name">Compound Name *</Label>
                  <CompoundSelect
                    value={form.compound_name}
                    onChange={(value) => setForm(prev => ({ ...prev, compound_name: value }))}
                    placeholder="Select or type compound..."
                    data-testid="protocol-compound-input"
                  />
                </div>

                {/* Dose */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="dose">Dose *</Label>
                    <Input
                      id="dose"
                      type="number"
                      step="any"
                      placeholder="250"
                      value={form.dose}
                      onChange={(e) => setForm(prev => ({ ...prev, dose: e.target.value }))}
                      className="h-12"
                      data-testid="protocol-dose-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Unit</Label>
                    <Select 
                      value={form.dose_unit} 
                      onValueChange={(v) => setForm(prev => ({ ...prev, dose_unit: v }))}
                    >
                      <SelectTrigger className="h-12" data-testid="protocol-unit-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DOSE_UNITS.map(unit => (
                          <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Frequency */}
                <div className="space-y-2">
                  <Label>Frequency</Label>
                  <Select 
                    value={form.frequency} 
                    onValueChange={(v) => setForm(prev => ({ ...prev, frequency: v }))}
                  >
                    <SelectTrigger className="h-12" data-testid="protocol-frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FREQUENCIES.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom Days */}
                {form.frequency === "custom" && (
                  <div className="space-y-2">
                    <Label>Select Days</Label>
                    <div className="flex flex-wrap gap-2">
                      {WEEKDAYS.map((day, index) => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => toggleCustomDay(index)}
                          className={`h-10 px-4 rounded-full text-sm font-medium transition-colors ${
                            form.custom_days.includes(index)
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground hover:bg-muted/80"
                          }`}
                          data-testid={`day-${day.toLowerCase()}`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Time */}
                <div className="space-y-2">
                  <Label htmlFor="time_of_day">Time of Day</Label>
                  <Input
                    id="time_of_day"
                    type="time"
                    value={form.time_of_day}
                    onChange={(e) => setForm(prev => ({ ...prev, time_of_day: e.target.value }))}
                    className="h-12"
                    data-testid="protocol-time-input"
                  />
                </div>

                {/* Start Date */}
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date</Label>
                  <DatePicker
                    value={form.start_date}
                    onChange={(date) => setForm(prev => ({ ...prev, start_date: date }))}
                    data-testid="protocol-start-date-input"
                  />
                </div>

                {/* Cycle Length */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cycle_length">Cycle Length</Label>
                    <Input
                      id="cycle_length"
                      type="number"
                      value={form.cycle_length}
                      onChange={(e) => setForm(prev => ({ ...prev, cycle_length: parseInt(e.target.value) || 0 }))}
                      className="h-12"
                      data-testid="protocol-cycle-length-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Unit</Label>
                    <Select 
                      value={form.cycle_unit} 
                      onValueChange={(v) => setForm(prev => ({ ...prev, cycle_unit: v }))}
                    >
                      <SelectTrigger className="h-12" data-testid="protocol-cycle-unit-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CYCLE_UNITS.map(unit => (
                          <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Any additional notes..."
                    value={form.notes}
                    onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="min-h-[80px]"
                    data-testid="protocol-notes-input"
                  />
                </div>
              </div>

              <DrawerFooter className="pt-2">
                <Button 
                  onClick={handleSubmit} 
                  disabled={submitting}
                  className="h-12 rounded-full"
                  data-testid="protocol-save-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    editingProtocol ? "Update Protocol" : "Create Protocol"
                  )}
                </Button>
                <DrawerClose asChild>
                  <Button variant="outline" className="h-12 rounded-full">Cancel</Button>
                </DrawerClose>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        </div>
      </header>

      {/* Content */}
      <main className="p-4 space-y-3">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : protocols.length > 0 ? (
          protocols.map((protocol, index) => (
            <Card 
              key={protocol.id} 
              className={`protocol-card animate-fadeIn stagger-${Math.min(index + 1, 5)} ${!protocol.is_active ? "opacity-60" : ""}`}
              style={{ opacity: 0 }}
              data-testid={`protocol-card-${index}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-lg">{protocol.compound_name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {protocol.dose} {protocol.dose_unit} • {FREQUENCIES.find(f => f.value === protocol.frequency)?.label}
                    </p>
                  </div>
                  <Badge variant={protocol.is_active ? "default" : "secondary"}>
                    {protocol.is_active ? "Active" : "Paused"}
                  </Badge>
                </div>

                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {protocol.time_of_day}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar size={14} />
                    {format(parseISO(protocol.start_date), "MMM d")} - {format(parseISO(protocol.end_date), "MMM d, yyyy")}
                  </span>
                </div>

                {protocol.notes && (
                  <p className="text-sm text-muted-foreground mb-3 italic">
                    {protocol.notes}
                  </p>
                )}

                {/* Protocol Progress Tracker */}
                {protocol.is_active && (() => {
                  const progress = calculateDoseProgress(protocol);
                  return (
                    <div className="mb-3 p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium flex items-center gap-1">
                          <TrendingUp size={12} className="text-primary" />
                          Protocol Progress
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Dose {progress.currentDose} of {progress.totalDoses}
                        </span>
                      </div>
                      <Progress value={progress.percent} className="h-2" />
                      <p className="text-xs text-muted-foreground mt-1 text-center">
                        {progress.percent}% Complete
                      </p>
                    </div>
                  );
                })()}

                <div className="flex items-center gap-2 pt-2 border-t border-border/50">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleActive(protocol)}
                    className="h-9 px-3 rounded-full"
                    data-testid={`protocol-toggle-${index}`}
                  >
                    {protocol.is_active ? <Pause size={16} /> : <Play size={16} />}
                    <span className="ml-1">{protocol.is_active ? "Pause" : "Resume"}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDrawer(protocol)}
                    className="h-9 px-3 rounded-full"
                    data-testid={`protocol-edit-${index}`}
                  >
                    <Edit size={16} />
                    <span className="ml-1">Edit</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDeleteDialog(protocol)}
                    className="h-9 px-3 rounded-full text-destructive hover:text-destructive"
                    data-testid={`protocol-delete-${index}`}
                  >
                    <Trash2 size={16} />
                    <span className="ml-1">Delete</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="animate-fadeIn">
            <CardContent className="empty-state">
              <FileText className="empty-state-icon" strokeWidth={1} />
              <h3 className="empty-state-title">No protocols yet</h3>
              <p className="empty-state-description">
                Create one to get started.
              </p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Protocol?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{protocolToDelete?.compound_name}</strong> and remove all its scheduled doses. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Protocol"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
